/**
 * LLM Monitoring Page
 * Main dashboard for monitoring and analyzing OpenRouter LLM requests
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Tab,
  Tabs,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  SelectChangeEvent
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  AttachMoney as MoneyIcon,
  Memory as TokenIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import llmMonitoring, { LLMRequest, LLMRequestFilters, LLMStats } from '../services/llm-monitoring';
import RequestList from '../components/llm/RequestList';
import ModelStats from '../components/llm/ModelStats';
import CostAnalytics from '../components/llm/CostAnalytics';
import RealTimeMonitor from '../components/llm/RealTimeMonitor';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const LLMMonitoring: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Request list state
  const [requests, setRequests] = useState<LLMRequest[]>([]);
  const [filters, setFilters] = useState<LLMRequestFilters>({
    limit: 100,
    offset: 0,
    orderBy: 'timestamp',
    orderDir: 'DESC'
  });
  
  // Statistics state
  const [stats, setStats] = useState<LLMStats[]>([]);
  const [modelStats, setModelStats] = useState<any>(null);
  const [totalMetrics, setTotalMetrics] = useState({
    totalRequests: 0,
    totalCost: 0,
    totalTokens: 0,
    avgLatency: 0,
    successRate: 0,
    cacheHitRate: 0
  });
  
  // Real-time monitoring state
  const [realtimeEnabled, setRealtimeEnabled] = useState(false);
  const [realtimeRequests, setRealtimeRequests] = useState<LLMRequest[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  
  // Date range state
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // 7 days ago
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Load initial data
  useEffect(() => {
    loadRequests();
    loadStats();
    loadModelStats();
  }, []);

  // Load requests with current filters
  const loadRequests = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await llmMonitoring.getRequests({
        ...filters,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString()
      });
      
      if (response.success && response.data) {
        setRequests(response.data);
      } else {
        setError(response.error || 'Failed to load requests');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const response = await llmMonitoring.getStats({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        groupBy: 'day',
        includeCacheStats: true
      });
      
      if (response.success && response.data) {
        setStats(response.data.aggregated);
        
        // Calculate total metrics (converting string values from API to numbers)
        const totals = response.data.aggregated.reduce((acc, stat) => ({
          totalRequests: acc.totalRequests + Number(stat.total_requests),
          totalCost: acc.totalCost + Number(stat.total_cost),
          totalTokens: acc.totalTokens + Number(stat.total_tokens),
          avgLatency: acc.avgLatency + Number(stat.avg_latency),
          successRate: acc.successRate + (Number(stat.successful_requests) / Number(stat.total_requests) * 100),
          cacheHitRate: acc.cacheHitRate + Number(stat.cache_hit_rate)
        }), {
          totalRequests: 0,
          totalCost: 0,
          totalTokens: 0,
          avgLatency: 0,
          successRate: 0,
          cacheHitRate: 0
        });
        
        // Calculate averages
        const count = response.data.aggregated.length || 1;
        totals.avgLatency = totals.avgLatency / count;
        totals.successRate = totals.successRate / count;
        totals.cacheHitRate = totals.cacheHitRate / count;
        
        setTotalMetrics(totals);
      }
    } catch (err: any) {
      console.error('Error loading stats:', err);
    }
  };

  // Load model statistics
  const loadModelStats = async () => {
    try {
      const response = await llmMonitoring.getModelStats();
      if (response.success && response.data) {
        setModelStats(response.data);
      }
    } catch (err: any) {
      console.error('Error loading model stats:', err);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key: keyof LLMRequestFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Toggle real-time monitoring
  const toggleRealtime = () => {
    if (realtimeEnabled) {
      // Stop monitoring
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      setRealtimeEnabled(false);
    } else {
      // Start monitoring
      const eventSource = llmMonitoring.connectRealtime(
        (data) => {
          if (data.type === 'llm_request') {
            setRealtimeRequests(prev => [data.data, ...prev].slice(0, 50)); // Keep last 50
          }
        },
        (error) => {
          console.error('Real-time monitoring error:', error);
          setError('Real-time monitoring connection lost');
        }
      );
      eventSourceRef.current = eventSource;
      setRealtimeEnabled(true);
    }
  };

  // Export data
  const handleExport = async (format: 'json' | 'csv') => {
    try {
      await llmMonitoring.exportData({
        ...filters,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        format
      });
    } catch (err: any) {
      setError('Failed to export data: ' + err.message);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            LLM Request Monitoring
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor and analyze all OpenRouter LLM API calls
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" variant="caption">
                      Total Requests
                    </Typography>
                    <Typography variant="h6">
                      {totalMetrics.totalRequests.toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" variant="caption">
                      Total Cost
                    </Typography>
                    <Typography variant="h6">
                      ${totalMetrics.totalCost.toFixed(4)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TokenIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" variant="caption">
                      Total Tokens
                    </Typography>
                    <Typography variant="h6">
                      {(totalMetrics.totalTokens / 1000).toFixed(1)}K
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <SpeedIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography color="textSecondary" variant="caption">
                      Avg Latency
                    </Typography>
                    <Typography variant="h6">
                      {totalMetrics.avgLatency.toFixed(0)}ms
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Success Rate
                  </Typography>
                  <Typography variant="h6" color={totalMetrics.successRate > 95 ? 'success.main' : 'warning.main'}>
                    {totalMetrics.successRate.toFixed(1)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent>
                <Box>
                  <Typography color="textSecondary" variant="caption">
                    Cache Hit Rate
                  </Typography>
                  <Typography variant="h6">
                    {totalMetrics.cacheHitRate.toFixed(1)}%
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Actions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Model</InputLabel>
                <Select
                  value={filters.model || ''}
                  onChange={(e: SelectChangeEvent) => handleFilterChange('model', e.target.value || undefined)}
                  label="Model"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="microsoft/phi-4-multimodal-instruct">Phi-4</MenuItem>
                  <MenuItem value="anthropic/claude-3.5-sonnet">Claude 3.5</MenuItem>
                  <MenuItem value="openai/gpt-4o-mini">GPT-4o Mini</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Request Type</InputLabel>
                <Select
                  value={filters.requestType || ''}
                  onChange={(e: SelectChangeEvent) => handleFilterChange('requestType', e.target.value || undefined)}
                  label="Request Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="routing">Tool Routing</MenuItem>
                  <MenuItem value="tool_call">Tool Call</MenuItem>
                  <MenuItem value="general">General</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  loadRequests();
                  loadStats();
                  loadModelStats();
                }}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button
                variant={realtimeEnabled ? 'outlined' : 'contained'}
                color={realtimeEnabled ? 'error' : 'primary'}
                startIcon={realtimeEnabled ? <StopIcon /> : <PlayIcon />}
                onClick={toggleRealtime}
                fullWidth
              >
                {realtimeEnabled ? 'Stop' : 'Start'} Real-time
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Main Content Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="Requests" />
              <Tab label="Analytics" />
              <Tab label="Models" />
              <Tab label="Real-time" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('csv')}
                    sx={{ mr: 1 }}
                  >
                    Export CSV
                  </Button>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={() => handleExport('json')}
                  >
                    Export JSON
                  </Button>
                </Box>
                <RequestList
                  requests={requests}
                  onRefresh={loadRequests}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                />
              </>
            )}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <CostAnalytics
              stats={stats}
              startDate={startDate}
              endDate={endDate}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <ModelStats modelStats={modelStats} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <RealTimeMonitor
              requests={realtimeRequests}
              enabled={realtimeEnabled}
              onToggle={toggleRealtime}
            />
          </TabPanel>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default LLMMonitoring;